import React, { useState, useCallback, useMemo } from 'react';
import { View, Text, Pressable, StyleSheet, ScrollView, ActivityIndicator } from 'react-native-web';
import { useNavigate } from 'react-router-dom';
import { getCampaignSlug } from '../services/CampaignService';

// Custom hook for notification handling logic
const useNotifications = (notifications) => {
  const navigate = useNavigate();
  const [loadingNotificationId, setLoadingNotificationId] = useState(null);
  const [error, setError] = useState(null);

  const handleNotificationRedirect = useCallback(async (notification) => {
    if (loadingNotificationId) return; // Prevent multiple clicks
    
    setLoadingNotificationId(notification.id);
    setError(null);
    
    try {
      if (notification.type === "CAMPAIGN_APPLIED" || notification.type === "CAMPAIGN_ACCEPTED") {
        const response = await getCampaignSlug(notification.user_id);
        if (response.status === 200 && response.data && response.data.slug) {
          navigate('/campaigns/' + response.data.slug);
        } else {
          throw new Error('Campaign not found');
        }
      } else if (notification.type === "CAMPAIGN_CONTENT_UPLOADED") {
        navigate('/content?user=' + notification.actor_id);
      }
    } catch (err) {
      console.error('Error navigating to notification:', err);
      setError('Failed to open notification. Please try again.');
    } finally {
      setLoadingNotificationId(null);
    }
  }, [navigate, loadingNotificationId]);

  return {
    handleNotificationRedirect,
    loadingNotificationId,
    error,
  };
};

// Individual notification item component
const NotificationItem = React.memo(({ notification, onPress, isLoading }) => {

  const getNotificationContent = useCallback((notification) => {
    switch (notification.type) {
      case "CAMPAIGN_CONTENT_UPLOADED":
        return {
          primaryText: `${notification.actor_first_name} has uploaded content`,
          secondaryText: notification.user_name,
          actionText: "View Content"
        };
      case "CAMPAIGN_APPLIED":
        return {
          primaryText: `${notification.actor_first_name} has applied to your campaign`,
          secondaryText: notification.user_name,
          actionText: "View Application"
        };
      case "CAMPAIGN_ACCEPTED":
        return {
          primaryText: `${notification.actor_first_name} has accepted your campaign invite`,
          secondaryText: notification.user_name,
          actionText: "View Campaign"
        };
      default:
        return {
          primaryText: "New notification",
          secondaryText: "",
          actionText: "View"
        };
    }
  }, []);

  const content = useMemo(() => getNotificationContent(notification), [notification, getNotificationContent]);

  return (
    <Pressable
      style={({ hovered, pressed }) => [
        styles.notificationItem,
        hovered && styles.notificationItemHovered,
        pressed && styles.notificationItemPressed,
        isLoading && styles.notificationItemLoading
      ]}
      onPress={() => onPress(notification)}
      disabled={isLoading}
      accessible={true}
      accessibilityRole="button"
      accessibilityLabel={`${content.primaryText} from ${content.secondaryText}. ${content.actionText}.`}
    >
      <View style={styles.notificationContent}>
        <View style={styles.notificationHeader}>
          <View style={styles.notificationTextContainer}>
            <Text style={styles.primaryText} numberOfLines={2}>
              {content.primaryText}
            </Text>
          </View>
          <View style={styles.notificationMeta}>
            {isLoading ? (
              <ActivityIndicator size="small" color="#396de0" />
            ) : (
              <Text style={styles.actionText}>{content.actionText}</Text>
            )}
          </View>
        </View>
        <View style={styles.notificationFooter}>
          {content.secondaryText && (
            <Text style={styles.secondaryText} numberOfLines={1}>
              {content.secondaryText}
            </Text>
          )}
          <Text style={styles.timeText}>{notification.elapsed_time}</Text>
        </View>
      </View>
    </Pressable>
  );
});

// Empty state component
const EmptyNotifications = React.memo(() => (
  <View style={styles.emptyContainer}>
    <Text style={styles.emptyIcon}>🔔</Text>
    <Text style={styles.emptyTitle}>No notifications yet</Text>
    <Text style={styles.emptyDescription}>
      When creators interact with your campaigns, you'll see notifications here.
    </Text>
  </View>
));

// Loading skeleton component
const NotificationSkeleton = React.memo(() => (
  <View style={styles.skeletonContainer}>
    {[1, 2, 3].map((index) => (
      <View key={index} style={styles.skeletonItem}>
        <View style={styles.skeletonContent}>
          <View style={styles.skeletonTextLong} />
          <View style={styles.skeletonTextShort} />
        </View>
      </View>
    ))}
  </View>
));

const NotificationsList = React.memo(({ notifications, isLoading, onRefresh }) => {
  const { handleNotificationRedirect, loadingNotificationId, error } = useNotifications(notifications);

  const sortedNotifications = useMemo(() => {
    if (!notifications || !Array.isArray(notifications)) return [];
    
    // Sort by most recent first
    return [...notifications].sort((a, b) => {
      // Assuming notifications have a timestamp or we can use ID as proxy
      return b.id - a.id;
    });
  }, [notifications]);



  if (isLoading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.title}>Notifications</Text>
        </View>
        <NotificationSkeleton />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Notifications</Text>
      </View>
      
      {error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      <View style={styles.notificationsContainer}>
        {(!sortedNotifications || sortedNotifications.length === 0) ? (
          <EmptyNotifications />
        ) : (
          <ScrollView 
            style={styles.scrollView}
            showsVerticalScrollIndicator={true}
            contentContainerStyle={styles.scrollContent}
          >
            {sortedNotifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onPress={handleNotificationRedirect}
                isLoading={loadingNotificationId === notification.id}
              />
            ))}
          </ScrollView>
        )}
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
    flex: 1,
    maxHeight: 400,
  },
  header: {
    marginBottom: 15,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#396de0',
    fontFamily: "'forma-djr-display', sans-serif",
  },

  errorContainer: {
    backgroundColor: '#fef2f2',
    padding: 12,
    borderRadius: 8,
    marginBottom: 15,
    borderLeftWidth: 4,
    borderLeftColor: '#ef4444',
  },
  errorText: {
    color: '#dc2626',
    fontSize: 14,
    fontFamily: "'lato', sans-serif",
  },
  notificationsContainer: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 10,
  },
  notificationItem: {
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 12,
    marginBottom: 6,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    transition: 'all 0.2s ease',
  },
  notificationItemHovered: {
    backgroundColor: '#f9fafb',
    borderColor: '#396de0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  notificationItemPressed: {
    backgroundColor: '#f3f4f6',
    transform: [{ scale: 0.98 }],
  },
  notificationItemLoading: {
    opacity: 0.7,
  },
  notificationContent: {
    flex: 1,
  },
  notificationHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },

  notificationTextContainer: {
    flex: 1,
    marginRight: 8,
  },
  primaryText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    fontFamily: "'lato', sans-serif",
    lineHeight: 18,
    marginBottom: 4,
  },
  secondaryText: {
    fontSize: 13,
    color: '#6b7280',
    fontFamily: "'lato', sans-serif",
    lineHeight: 16,
  },
  notificationMeta: {
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 60,
  },
  actionText: {
    fontSize: 12,
    color: '#396de0',
    fontWeight: '600',
    fontFamily: "'lato', sans-serif",
  },
  notificationFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 4,
  },
  timeText: {
    fontSize: 11,
    color: '#9ca3af',
    fontFamily: "'lato', sans-serif",
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: 16,
    opacity: 0.5,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#374151',
    fontFamily: "'forma-djr-display', sans-serif",
    marginBottom: 8,
  },
  emptyDescription: {
    fontSize: 14,
    color: '#6b7280',
    fontFamily: "'lato', sans-serif",
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 280,
  },
  skeletonContainer: {
    flex: 1,
  },
  skeletonItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    marginBottom: 6,
    borderRadius: 8,
    backgroundColor: '#f9fafb',
  },
  skeletonContent: {
    flex: 1,
  },
  skeletonTextLong: {
    height: 14,
    backgroundColor: '#e5e7eb',
    borderRadius: 4,
    marginBottom: 6,
    width: '80%',
  },
  skeletonTextShort: {
    height: 12,
    backgroundColor: '#e5e7eb',
    borderRadius: 4,
    width: '50%',
  },
});

NotificationItem.displayName = 'NotificationItem';
EmptyNotifications.displayName = 'EmptyNotifications';
NotificationSkeleton.displayName = 'NotificationSkeleton';
NotificationsList.displayName = 'NotificationsList';

export default NotificationsList; 